import { useQuery } from "@tanstack/react-query";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { useState } from "react";
import { ICommonServiceProps } from "@api/types/globalService.types";
import { urlUtility } from "@repo/utilities/url.utility.ts";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";

export const useGetAllEndorsementsService = ({
  page = "1",
  per_page = "15",
  enableQuery = true,
  filters = {},
  cacheKeys = [],
}: ICommonServiceProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const certificate_no = urlQueries["certificate-no"] ?? "";
  const proposer_name = urlQueries["proposer-name"] ?? "";

  const allEndorsementsService = useQuery({
    queryKey: [
      ...CACHE_KEYS.ENDORSEMENTS,
      page,
      per_page,
      debouncedQuery,
      certificate_no,
      proposer_name,
      JSON.stringify(filters),
      ...cacheKeys,
    ],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.ENDORSEMENTS,
        searchParams: {
          page,
          per_page,
          "filter[certificate_no]": certificate_no,
          "filter[certificate_enquiry_proposer_name]": proposer_name,
          include:
            "certificate,partner,certificate.providerable,certificate.enquiry.proposer",
          ...filters,
        },
      }),
    enabled: enableQuery,
  });

  return {
    services: { allEndorsementsService },
    states: { searchQuery, setSearchQuery },
  };
};

export const useGetSingleEndorsementService = () => {
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const endorsementId = urlQueries["endorsement-id"];
  const singleEndorsementsService = useQuery({
    queryKey: [...CACHE_KEYS.ENDORSEMENTS, endorsementId],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.ENDORSEMENTS}/${endorsementId}`,
        searchParams: {
          include: "certificate,partner",
        },
      }),
    enabled: !!endorsementId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });

  return {
    services: { singleEndorsementsService },
  };
};
